import React, { useEffect, useRef, useState } from 'react';

export const SWIPE_THRESHOLD = 100;
export const swipeChoice = (dx, dy) => Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.5 ? (dx > 0 ? 'A' : 'B') : null;

// Pointer events cover touch, pen and mouse. Native vertical scrolling stays enabled.
export function SwipeCard({ children, disabled = false, onChoose, optionA, optionB }) {
  const gesture = useRef(null);
  const [offset, setOffset] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(Boolean(query?.matches));
    update(); query?.addEventListener?.('change', update);
    return () => query?.removeEventListener?.('change', update);
  }, []);
  useEffect(() => { if (disabled) { gesture.current = null; setOffset(0); } }, [disabled]);
  const reset = () => { gesture.current = null; setOffset(0); };
  const start = event => {
    if (disabled || event.isPrimary === false || (event.button !== undefined && event.button !== 0) || event.target.closest('button,a,input,textarea,select,summary,details')) return;
    gesture.current = { x: event.clientX, y: event.clientY, id: event.pointerId, axis: null };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const move = event => {
    const current = gesture.current;
    if (!current || current.id !== event.pointerId || disabled) return;
    const dx = event.clientX - current.x, dy = event.clientY - current.y;
    if (!current.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 10) current.axis = Math.abs(dx) > Math.abs(dy) * 1.5 ? 'x' : 'y';
    if (current.axis === 'x') setOffset(dx);
  };
  const end = event => {
    const current = gesture.current;
    reset();
    if (!current || current.id !== event.pointerId || disabled || current.axis === 'y') return;
    const choice = swipeChoice(event.clientX - current.x, event.clientY - current.y);
    if (choice) onChoose(choice);
  };
  const direction = offset >= 0 ? 'A' : 'B';
  return <div className="relative" data-testid="swipe-card-shell">
    <div aria-hidden="true" className="absolute inset-x-3 -bottom-2 h-16 rounded-3xl border border-slate-700 bg-slate-800/60" />
    <div data-testid="swipe-card" onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerCancel={reset} onLostPointerCapture={reset}
      style={{ touchAction: 'pan-y', transform: reducedMotion ? 'none' : `translateX(${Math.max(-110, Math.min(110, offset))}px) rotate(${Math.max(-8, Math.min(8, offset / 18))}deg)`, transition: offset ? 'none' : reducedMotion ? 'none' : 'transform 180ms ease-out' }}
      className={`relative ${disabled ? '' : 'cursor-grab active:cursor-grabbing'} rounded-3xl`}>
      {Math.abs(offset) > 15 && <div aria-hidden="true" data-testid="swipe-direction" className={`pointer-events-none absolute top-5 z-20 max-w-[75%] px-4 py-2 rounded-xl border-2 font-extrabold shadow-lg ${direction === 'A' ? 'right-5 bg-emerald-950 text-emerald-200 border-emerald-400' : 'left-5 bg-rose-950 text-rose-200 border-rose-400'}`} style={{ opacity: Math.min(1, Math.abs(offset) / SWIPE_THRESHOLD) }}>{direction === 'A' ? `${optionA} →` : `← ${optionB}`}</div>}
      {children}
    </div>
  </div>;
}
